import type { Locale } from "./types";
import {
  buildDynamicBooksStructuredMediaUrlForOrigin
} from "../../server/structuredMediaRegistry.js";
import {
  dbkListPublicSoftwareTiers,
  type DbkCountrySoftwareTier,
  listDynamicBooksPublicSoftwareTiers,
  type DynamicBooksPublicTierKey
} from "../../server/billing/dynamicBooksPublicPricing";
import { softwareTierStructuredMediaAssetByKey } from "./structuredMedia";

export type SoftwareTierKey = DynamicBooksPublicTierKey;

export type SoftwareTier = {
  key: SoftwareTierKey;
  billingTierKey: "dynamicbooks_basic" | "dynamicbooks_business" | "dynamicbooks_premium";
  name: Record<Locale, string>;
  priceMonthlySek: 149 | 249 | 399;
  summary: Record<Locale, string>;
  includedUsage: Record<Locale, string>;
  highlights: Record<Locale, string[]>;
  entitlementSummary: Record<Locale, string[]>;
};

export type SoftwareFeatureRow = {
  key:
    | "core_books"
    | "vat_reports"
    | "bank_sie"
    | "ap_ar"
    | "payroll"
    | "data_rooms"
    | "mcp"
    | "agent_preparation";
  feature: Record<Locale, string>;
  values: Record<SoftwareTierKey, Record<Locale, string>>;
};

export type SoftwarePricingContent = {
  id: "software-pricing";
  eyebrow: string;
  title: string;
  lead: string;
  priceNote: string;
  ctaLabel: string;
  matrixTitle: string;
  notesTitle: string;
  notes: string[];
};

const softwareTierCopy = {
  basic: {
    summary: {
      sv: "För bolag som vill ha kärnfunktioner för bokföring, moms och rapporter utan serviceåtagande.",
      en: "For companies that need core bookkeeping, VAT, and reports without a service commitment."
    },
    includedUsage: {
      sv: "Kärnåkomst till programvara för en bolagsyta.",
      en: "Core software access for one company workspace."
    },
    highlights: {
      sv: ["BAS och kontosaldon", "Momsunderlag", "Grundrapporter"],
      en: ["BAS and account balances", "VAT material", "Core reports"]
    },
    entitlementSummary: {
      sv: ["1 användare", "Lätt Data Room-struktur", "Uppgraderingspromptar när moduler kräver högre nivå"],
      en: ["1 included user", "Light Data Room structure", "Upgrade prompts when modules require a higher tier"]
    }
  },
  business: {
    summary: {
      sv: "För återkommande arbete med bankkällor, AP/AR, Data Rooms och tydligare kontrollflöden.",
      en: "For recurring work with bank sources, AP/AR, Data Rooms, and clearer control workflows."
    },
    includedUsage: {
      sv: "Utökad programvaruåtkomst för fler arbetsströmmar och spårbara underlag.",
      en: "Expanded software access for more workstreams and traceable evidence."
    },
    highlights: {
      sv: ["Bank och SIE-flöden", "AP/AR-översikt", "Data Rooms och löneöversikt"],
      en: ["Bank and SIE flows", "AP/AR overview", "Data Rooms and payroll overview"]
    },
    entitlementSummary: {
      sv: ["3 användare", "Standardrapporter", "Guidade MCP-arbetsflöden"],
      en: ["3 included users", "Standard reports", "Guided MCP workflows"]
    }
  },
  premium: {
    summary: {
      sv: "För mer rapportintensiv drift med avancerade arbetsflöden, evidens och agentförberedelser.",
      en: "For more report-intensive operations with advanced workflows, evidence, and agent preparation."
    },
    includedUsage: {
      sv: "Avancerad programvaruåtkomst för fler kontroller, rapportpaket och arbetsflöden.",
      en: "Advanced software access for more controls, report packages, and workflows."
    },
    highlights: {
      sv: ["Rapportpaket", "Agentförberedelse", "Lönebearbetning och MCP"],
      en: ["Report packages", "Agent preparation", "Payroll processing and MCP"]
    },
    entitlementSummary: {
      sv: ["5 användare", "Avancerade Data Rooms", "SIE, anläggningar och kontrollspår"],
      en: ["5 included users", "Advanced Data Rooms", "SIE, fixed assets, and control trails"]
    }
  }
} satisfies Record<SoftwareTierKey, Omit<SoftwareTier, "key" | "billingTierKey" | "name" | "priceMonthlySek">>;

export const softwarePricingTiers: SoftwareTier[] = listDynamicBooksPublicSoftwareTiers().map((tier) => ({
  key: tier.publicKey,
  billingTierKey: `dynamicbooks_${tier.publicKey}` as SoftwareTier["billingTierKey"],
  name: { sv: tier.displayName, en: tier.displayName },
  priceMonthlySek: tier.monthlyPriceSek,
  ...softwareTierCopy[tier.publicKey]
}));

const dbkUsSoftwareTierCopy = {
  basic: {
    summary: "Core bookkeeping software for one company workspace.",
    highlights: ["Bookkeeping records and account balances", "Core reports", "1 included user"]
  },
  business: {
    summary: "More workflows for recurring bookkeeping and document review.",
    highlights: ["Accounts payable and receivable overview", "Data Rooms and supporting evidence", "Standard reports and 3 included users"]
  },
  premium: {
    summary: "Advanced reporting, evidence controls, and agent preparation.",
    highlights: ["Report packages and control trails", "Advanced Data Rooms", "Agent preparation and 5 included users"]
  }
} satisfies Record<SoftwareTierKey, { summary: string; highlights: string[] }>;

export const dbkUsSoftwarePricingTiers = dbkListPublicSoftwareTiers("US").map((tier) => ({
  ...tier,
  ...dbkUsSoftwareTierCopy[tier.public_key]
}));

export function dbkFormatCountrySoftwarePrice(tier: DbkCountrySoftwareTier): string {
  const amount = tier.amount_minor / 10 ** tier.currency_minor_unit;
  return new Intl.NumberFormat(tier.country_code === "US" ? "en-US" : "sv-SE", {
    style: "currency",
    currency: tier.currency_code,
    maximumFractionDigits: 0
  }).format(amount);
}

export const softwareFeatureRows: SoftwareFeatureRow[] = [
  {
    key: "core_books",
    feature: { sv: "BAS, konton och saldon", en: "BAS, accounts, and balances" },
    values: {
      basic: { sv: "Kärna", en: "Core" },
      business: { sv: "Utökad", en: "Expanded" },
      premium: { sv: "Avancerad", en: "Advanced" }
    }
  },
  {
    key: "vat_reports",
    feature: { sv: "Moms och rapportunderlag", en: "VAT and report material" },
    values: {
      basic: { sv: "Grund", en: "Basic" },
      business: { sv: "Periodflöden", en: "Period workflows" },
      premium: { sv: "Rapportpaket", en: "Report packages" }
    }
  },
  {
    key: "bank_sie",
    feature: { sv: "Bankkällor och SIE", en: "Bank sources and SIE" },
    values: {
      basic: { sv: "Manuellt underlag", en: "Manual material" },
      business: { sv: "Arbetsflöden", en: "Workflows" },
      premium: { sv: "Kontrollspår", en: "Control trail" }
    }
  },
  {
    key: "ap_ar",
    feature: { sv: "Kund- och leverantörsflöden", en: "Customer and supplier flows" },
    values: {
      basic: { sv: "Ej huvudfokus", en: "Not primary" },
      business: { sv: "Översikt", en: "Overview" },
      premium: { sv: "Kontrollspår", en: "Control trail" }
    }
  },
  {
    key: "payroll",
    feature: { sv: "Lönehantering", en: "Payroll processing" },
    values: {
      basic: { sv: "Uppgraderingsprompt", en: "Upgrade prompt" },
      business: { sv: "Payroll-översikt", en: "Payroll overview" },
      premium: { sv: "Processing och journalspår", en: "Processing and journal trail" }
    }
  },
  {
    key: "data_rooms",
    feature: { sv: "Data Rooms och evidens", en: "Data Rooms and evidence" },
    values: {
      basic: { sv: "Lätt struktur", en: "Light structure" },
      business: { sv: "Ingår", en: "Included" },
      premium: { sv: "Utökad", en: "Expanded" }
    }
  },
  {
    key: "mcp",
    feature: { sv: "MCP och agentarbetsflöden", en: "MCP and agent workflows" },
    values: {
      basic: { sv: "Uppgraderingsprompt", en: "Upgrade prompt" },
      business: { sv: "Guidat", en: "Guided" },
      premium: { sv: "Fullt arbetsflöde", en: "Full workflow" }
    }
  },
  {
    key: "agent_preparation",
    feature: { sv: "Agentförberedelse", en: "Agent preparation" },
    values: {
      basic: { sv: "Planeringssignal", en: "Planning signal" },
      business: { sv: "Torrkörningar", en: "Dry-runs" },
      premium: { sv: "Fler arbetsflöden", en: "More workflows" }
    }
  }
];

export const softwarePricingContent = {
  sv: {
    id: "software-pricing",
    eyebrow: "Programvara",
    title: "Publika programvarupriser",
    lead:
      "Basic, Business och Premium är programvaruabonnemang för DynamicBooks. Servicearbete, Agent credits och uppstartsomfattning uppskattas separat.",
    priceNote: "SEK per månad exklusive moms.",
    ctaLabel: "Starta abonnemang",
    matrixTitle: "Funktionsmatris för programvaran",
    notesTitle: "Viktiga gränser",
    notes: [
      "Priserna gäller programvaruåtkomst och är inte en bokföringsservice eller ett faktureringsbeslut.",
      "Service, migrering, Agent credits och uppstartsomfattning beräknas i uppskattningsflödet.",
      "Abonnemang startas via autentiserad DynamicMail Checkout med Stripe Tax och moms-ID-insamling.",
      "Programvaruåtkomst levereras digitalt utan fysisk frakt eller fraktavgift och är inte returnerbar när åtkomsten har provisionerats."
    ]
  },
  en: {
    id: "software-pricing",
    eyebrow: "Software",
    title: "Public software pricing",
    lead:
      "Basic, Business, and Premium are DynamicBooks software subscriptions. Service work, Agent credits, and onboarding scope are estimated separately.",
    priceNote: "SEK per month excluding VAT.",
    ctaLabel: "Start subscription",
    matrixTitle: "Software feature matrix",
    notesTitle: "Important boundaries",
    notes: [
      "Prices apply to software access and are not an accounting service or final billing decision.",
      "Service, migration, Agent credits, and onboarding scope are calculated in the estimate flow.",
      "Subscriptions start through authenticated DynamicMail Checkout with Stripe Tax and VAT ID collection.",
      "Software access is delivered digitally with no physical shipment or shipping charge and is not returnable once provisioned."
    ]
  }
} satisfies Record<Locale, SoftwarePricingContent>;

export function formatSoftwareTierPrice(tier: SoftwareTier, locale: Locale): string {
  const formatter = new Intl.NumberFormat(locale === "sv" ? "sv-SE" : "en-SE", {
    maximumFractionDigits: 0
  });
  const amount = formatter.format(tier.priceMonthlySek);

  return locale === "sv" ? `${amount} kr/mån` : `${amount} SEK/month`;
}

export function buildSoftwareTierOfferNodes(locale: Locale, catalogUrl: string, idBaseUrl: string): Array<Record<string, unknown>> {
  return softwarePricingTiers.map((tier, index) => {
    const offerId = `${idBaseUrl}/#offer-${tier.key}`;
    const productId = `${idBaseUrl}/#product-${tier.key}`;
    const offerDetails = buildSoftwareTierOffer(locale, catalogUrl, idBaseUrl, tier);

    return {
      ...offerDetails,
      position: index + 1,
      itemOffered: {
        "@id": productId
      }
    };
  });
}

export function buildSoftwareTierProductNodes(locale: Locale, catalogUrl: string, idBaseUrl: string): Array<Record<string, unknown>> {
  return softwarePricingTiers.map((tier) => buildSoftwareTierProduct(locale, catalogUrl, idBaseUrl, tier));
}

export function buildPrimarySoftwareApplicationOffer(locale: Locale, catalogUrl: string, idBaseUrl: string): Record<string, unknown> {
  const offer = buildSoftwareTierOfferNodes(locale, catalogUrl, idBaseUrl)[0];

  if (!offer) {
    throw new Error("DynamicBooks software pricing requires at least one public tier.");
  }

  return offer;
}

export function buildSoftwareTierOfferCatalog(locale: Locale, catalogUrl: string, idBaseUrl: string): Record<string, unknown> {
  return {
    "@type": "OfferCatalog",
    "@id": `${idBaseUrl}/#software-tier-offers`,
    name: locale === "sv" ? "DynamicBooks programvaruabonnemang" : "DynamicBooks software subscriptions",
    itemListElement: buildSoftwareTierOfferNodes(locale, catalogUrl, idBaseUrl)
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function buildSoftwareTierProduct(
  locale: Locale,
  catalogUrl: string,
  idBaseUrl: string,
  tier: SoftwareTier
): Record<string, unknown> {
  return {
    "@type": "Product",
    "@id": `${idBaseUrl}/#product-${tier.key}`,
    name: `${tier.name[locale]} DynamicBooks`,
    url: catalogUrl,
    image: buildSoftwareTierImageUrl(idBaseUrl, tier),
    description: tier.summary[locale],
    brand: buildSoftwareTierBrand(idBaseUrl),
    category: locale === "sv" ? "Bokföringsprogram" : "Accounting software",
    offers: buildSoftwareTierOffer(locale, catalogUrl, idBaseUrl, tier)
  };
}

function buildSoftwareTierOffer(
  locale: Locale,
  catalogUrl: string,
  idBaseUrl: string,
  tier: SoftwareTier
): Record<string, unknown> {
  const priceSpecification = {
    "@type": "UnitPriceSpecification",
    price: tier.priceMonthlySek,
    priceCurrency: "SEK",
    unitText: locale === "sv" ? "månad exklusive moms" : "month excluding VAT"
  };

  return {
    "@type": "Offer",
    "@id": `${idBaseUrl}/#offer-${tier.key}`,
    name: `${tier.name[locale]} DynamicBooks`,
    url: catalogUrl,
    price: tier.priceMonthlySek,
    priceCurrency: "SEK",
    availability: "https://schema.org/InStock",
    itemCondition: "https://schema.org/NewCondition",
    priceSpecification,
    shippingDetails: buildDigitalSoftwareShippingDetails(locale),
    hasMerchantReturnPolicy: buildDigitalSoftwareReturnPolicy(locale, catalogUrl)
  };
}

function buildSoftwareTierBrand(idBaseUrl: string): Record<string, unknown> {
  return {
    "@type": "Brand",
    "@id": `${idBaseUrl}/#brand`,
    name: "DynamicBooks",
    url: `${idBaseUrl}/`,
    logo: new URL("/assets/brand/v3/dynamicbooks-mark-dark.svg", `${idBaseUrl}/`).toString()
  };
}

function buildSoftwareTierImageUrl(idBaseUrl: string, tier: SoftwareTier): string {
  return buildDynamicBooksStructuredMediaUrlForOrigin(
    softwareTierStructuredMediaAssetByKey[tier.key],
    "png",
    idBaseUrl
  );
}

function buildDigitalSoftwareShippingDetails(locale: Locale): Record<string, unknown> {
  return {
    "@type": "OfferShippingDetails",
    shippingRate: {
      "@type": "MonetaryAmount",
      value: "0.00",
      currency: "SEK"
    },
    shippingDestination: {
      "@type": "DefinedRegion",
      addressCountry: "SE"
    },
    deliveryTime: {
      "@type": "ShippingDeliveryTime",
      handlingTime: {
        "@type": "QuantitativeValue",
        minValue: 0,
        maxValue: 1,
        unitCode: "DAY"
      },
      transitTime: {
        "@type": "QuantitativeValue",
        minValue: 0,
        maxValue: 0,
        unitCode: "DAY"
      }
    },
    description: locale === "sv" ? "Digital leverans utan fysisk frakt." : "Digital delivery with no physical shipment."
  };
}

function buildDigitalSoftwareReturnPolicy(locale: Locale, catalogUrl: string): Record<string, unknown> {
  return {
    "@type": "MerchantReturnPolicy",
    applicableCountry: "SE",
    returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
    merchantReturnLink: catalogUrl,
    name: locale === "sv" ? "Digital åtkomst kan inte returneras efter provisionering" : "Digital access is not returnable after provisioning"
  };
}
